import { ref, watch, onBeforeMount } from '@vue/composition-api'
import i18n from '@hw-core/libs/i18n'
import router from '@/shared/router'
import store from '@/shared/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { db } from '@/shared/services/firebase'
import { updateUser } from '@/shared/services/users'

// Notification

export default function usePersonelView() {
  const userData = ref(null)
  const toast = useToast()

  const refShiftsUserListTable = ref(null)
  const selectedTempContractIndex = ref(-1)

  // Table Handlers
  const tableColumns = [
    { key: 'title', label: i18n.t('name'), sortable: true },
    { key: 'start', label: i18n.t('start'), sortable: true },
    { key: 'end', label: i18n.t('end'), sortable: true },
    { key: 'requirements', label: i18n.t('personel.table.headers.requirements'), sortable: true },
    { key: 'status', label: i18n.t('status'), sortable: true },
  ]

  // Table Temp Contract
  const tableTemContractColumns = [
    { key: 'contract.name', label: i18n.t('personel.table.headers.contractName'), sortable: true },
    { key: 'area.location.sectionInfo', label: 'Pääalue', sortable: true },
    { key: 'area', label: 'Lisäalueet' },
    { key: 'startDate', label: i18n.t('personel.table.headers.start_date'), sortable: true },
    { key: 'endDate', label: i18n.t('personel.table.headers.end_date'), sortable: true },
    { key: 'processer', label: i18n.t('personel.table.headers.processer'), sortable: true },
    { key: 'actions', label: i18n.t('personel.table.headers.actions') },
  ]

  const perPage = ref(10)
  const currentPage = ref(1)
  const statusFilter = ref({ label: i18n.t('personel.filters.confirmed'), value: 'confirmed' })
  const loadMore = ref(true)
  const canLoadMore = ref(false)
  const amount = ref(0)
  const totalHours = ref(0)
  let shifts = []
  const processer = ref(null)
  const isEventHandlerSidebarActive = ref(false)
  const blankEvent = {
    title: '',
    start: '',
    end: '',
    allDay: false,
    url: '',
    reserved: false,
    extendedProps: {
      calendar: '',
      guests: [],
      location: '',
      description: '',
      kilometrikorvaus: 0,
      workDayType: '',
      updates: [],
      boosted: false,
      boostedOptions: {},
    },
  }
  const event = ref(JSON.parse(JSON.stringify(blankEvent)))

  onBeforeMount(() => {
    store.dispatch('appAuth/getCurrentUser').then(res => {
      const user = JSON.parse(JSON.stringify(res))
      processer.value = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }
    })
  })

  const refetchData = () => {
    refShiftsUserListTable.value.refresh()
  }

  const updateData = () => {
    shifts = []
    refShiftsUserListTable.value.refresh()
  }

  watch([currentPage, statusFilter], (newVal, oldVal) => {
    if (newVal[0] === oldVal[0]) {
      shifts = []
    }
    refetchData()
  })

  const fetchUser = () => {
    store
      .dispatch('appPersonel/fetchUser', { id: router.currentRoute.params.id })
      .then(async response => {
        userData.value = response
        userData.value.id = router.currentRoute.params.id
        if (response.tempContract && response.tempContract.length > 0) {
          userData.value.tempContract = await Promise.all(
            response.tempContract.map(async item => {
              const area = await db
                .collection('areas')
                .doc(item.areaId)
                .get()
              const contract = await db
                .collection('contracts')
                .doc(item.contractId)
                .get()
              const areasCollection = await db
                .collection('areas')
                .where('accountId', '==', area.data().accountId)
                .get()
              return {
                ...item,
                area: area.exists
                  ? {
                    ...area.data(),
                    id: area.id,
                  }
                  : { name: '' },
                areaOptions: area.exists
                  ? areasCollection.docs.map(
                    obj => (
                      {
                        ...obj.data(),
                        id: obj.id
                      }
                    ))
                  : [],
                contract: contract.exists
                  ? {
                    ...contract.data(),
                    id: contract.id,
                  }
                  : { name: '' },
              }
            })
          )
        }
      })
      .catch(error => {
        if (error.response.status === 404) {
          userData.value = undefined
        }
      })
  }

  const convertToEventApi= item => {
    const {
      id,
      title,
      start,
      startStr,
      endStr,
      end,
      status,
      // eslint-disable-next-line object-curly-newline
      location,
      gigId,
      areaId,
      accountId,
      isDraft,
      assignedTo,
      requirements,
      reserved,
      kilometrikorvaus,
      workDayType,
      updates,
      boosted,
      calendar,
      boostedOptions,
    } = item

    return {
      id,
      title,
      start,
      startStr,
      endStr,
      end,
      status,
      extendedProps: {
        calendar,
        isDraft: isDraft || false,
        location,
        gigId,
        areaId,
        accountId,
        assignedTo,
        requirements: requirements || [],
        reserved,
        kilometrikorvaus: kilometrikorvaus || 0,
        workDayType,
        updates: updates || [],
        boosted: boosted || false,
        boostedOptions: boostedOptions || {},
      },
      allDay: false,
    }
  }

  const openShift = item => {
    event.value = convertToEventApi(item)
    isEventHandlerSidebarActive.value = true
  }

  const fetchShiftsPersonelId = (ctx, callback) => {
    store
      .dispatch('appPersonel/getShiftsByPersonelId', {
        personelId: userData.value.id,
        status: statusFilter.value.value,
        limit: perPage.value,
        list: shifts,
      })
      .then(({ data, isMore, amountOfShifts, hours }) => {
        shifts = data
        canLoadMore.value = isMore
        loadMore.value = false
        amount.value = amountOfShifts
        totalHours.value = hours
        callback(shifts)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe vuorojen hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const shiftDeleteBoosted = ref(null)

  const confirmDelete = () => {
    const newUpdate = {
      state: 'boosted',
      content: ['Boosted deleted'],
      processer: `${processer.value.firstName} ${processer.value.lastName}`,
      date: new Date(),
    }
    store
      .dispatch('appPersonel/updateShift', {
        id: shiftDeleteBoosted.value.id,
        data: {
          boosted: false,
          boostedOptions: null,
        },
        newUpdate
      })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Vuoro päivitetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        shifts = []
        shiftDeleteBoosted.value = null
        refetchData()
      })
  }

  const removeTempContract = () => {
    if (selectedTempContractIndex.value > -1) {
      userData.value.tempContract.splice(selectedTempContractIndex.value, 1)
      const newTempContracts = userData.value.tempContract
      updateUser(userData.value.id, { tempContract: newTempContracts })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('success'),
              text: 'Sijaisuussopimus poistettu',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          fetchUser()
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: i18n.t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
  }

  return {
    userData,
    statusFilter,
    refShiftsUserListTable,
    tableColumns,
    currentPage,
    loadMore,
    canLoadMore,
    amount,
    totalHours,
    refetchData,
    fetchUser,
    fetchShiftsPersonelId,
    processer,
    shiftDeleteBoosted,
    removeTempContract,
    selectedTempContractIndex,
    confirmDelete,
    openShift,
    event,
    isEventHandlerSidebarActive,
    tableTemContractColumns,
    updateData,
  }
}
